import React, { ReactNode, useCallback, useRef, useState } from "react";
import { dailyApi } from "@/features/daily/api/daily.api";
import type { DailyIncidentCommandDto } from "@/features/daily/types/daily.types";
import { toDailyError } from "@/features/daily/hooks/use-daily-resource";
import type { IncidentVm } from "@/features/daily/components/command/command-view-model";
import { IncidentCard } from "@/features/daily/components/incidents/IncidentCard";
import {
    IncidentAction,
    IncidentCommandIntent,
    buildCommand,
    conflictCopy,
    createIdempotencyKey,
    intentKey,
} from "@/features/daily/components/incidents/incident-command";

type Props = {
    incidents: IncidentVm[];
    viewerId: string;
    onApplied: () => void;
    /** What the panel says when nothing is open; the caller knows whether that claim holds. */
    empty: ReactNode;
};

const FORBIDDEN_COPY = "Tu cuenta no puede actuar sobre incidencias.";

// Resolved at call time, never destructured at import time: the module object is
// the seam the specs stub, and a captured reference would bypass it.
const send = async (action: IncidentAction, command: DailyIncidentCommandDto): Promise<void> => {
    if (action === "acknowledge") { await dailyApi.acknowledge(command); return; }
    if (action === "assign") { await dailyApi.assign(command); return; }
    await dailyApi.unassign(command);
};

export const IncidentsPanel: React.FC<Props> = ({ incidents, viewerId, onApplied, empty }) => {
    // C4: the key lives in component state and survives every retry of the same
    // intent. A ref is used because the very next click must read the key that the
    // previous attempt created, before any re-render has happened.
    const keysByIntent = useRef(new Map<string, string>());
    const [pending, setPending] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const act = useCallback(async (incident: IncidentVm, action: IncidentAction): Promise<void> => {
        const intent: IncidentCommandIntent = {
            incidentId: incident.incidentId,
            expectedEpisode: incident.episode,
            // C12: assignment is self-service. The only assignee this UI can name is
            // the principal that is signed in.
            assignee: action === "assign" ? viewerId : null,
            action,
        };
        const key = intentKey(intent);
        const existing = keysByIntent.current.get(key);
        const idempotencyKey = existing ?? createIdempotencyKey();
        keysByIntent.current.set(key, idempotencyKey);

        setPending(key);
        setErrors((current) => {
            const next = { ...current };
            delete next[incident.incidentId];
            return next;
        });

        try {
            await send(action, buildCommand(intent, idempotencyKey));
            // Only a success retires the key: anything else may still have landed.
            keysByIntent.current.delete(key);
            onApplied();
        } catch (error) {
            const dailyError = toDailyError(error);
            // A 409 tells the operator what to DO about it; anything else is reported
            // as itself rather than dressed up as a conflict they could act on.
            const copy = conflictCopy(dailyError)
                ?? (dailyError.code === "forbidden" ? FORBIDDEN_COPY : dailyError.message);
            setErrors((current) => ({ ...current, [incident.incidentId]: copy }));
        } finally {
            setPending(null);
        }
    }, [onApplied, viewerId]);

    if (incidents.length === 0) return <>{empty}</>;

    return (
        <ul className="daily-incidents">
            {incidents.map((incident) => {
                const prefix = `${incident.incidentId}|${incident.episode}|`;
                const pendingAction = pending !== null && pending.startsWith(prefix)
                    ? pending.slice(prefix.length)
                    : null;
                return (
                    <IncidentCard
                        key={incident.incidentId}
                        incident={incident}
                        pendingAction={isIncidentAction(pendingAction) ? pendingAction : null}
                        error={errors[incident.incidentId] ?? null}
                        onAct={(action) => { void act(incident, action); }}
                    />
                );
            })}
        </ul>
    );
};

const isIncidentAction = (value: string | null): value is IncidentAction =>
    value === "acknowledge" || value === "assign" || value === "unassign";
